import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useCopyToBuffer } from "hooks/useCopyToBuffer";
import { useNotification } from "hooks/notifications/useNotification";
import { FeaturedDcExtendedGeometry } from "../../../ducks/selectFeatures/types";

const getGeoJsonFormat = (data: FeaturedDcExtendedGeometry) => {
  const { type, coordinates } = data;
  const geoJson = {
    geometry: {
      type,
      coordinates,
    },
  };

  return JSON.stringify(geoJson, null, "\t");
};

const getWktFormat = (data: FeaturedDcExtendedGeometry) => {
  const { type, coordinates } = data;

  return `${type.toUpperCase()} (${coordinates.toString()})`;
};

const getKmlFormat = (data: FeaturedDcExtendedGeometry) => {
  const { type, coordinates } = data;

  const template = `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
     <Placemark>
      <${type}>
        <coordinates>${coordinates}</coordinates>
      </${type}>
    </Placemark>
  </kml>`;

  return template;
};

export const useCalculatedAttributesVertex = (value: FeaturedDcExtendedGeometry, format: string) => {
  const { t } = useTranslation("dialogs");
  const input = useRef<HTMLTextAreaElement>(null);
  const { copyContentToBuffer } = useCopyToBuffer(input);
  const [dataWasCopied, setCopied] = useState(false);

  useNotification(dataWasCopied, {
    success: true,
    title: t("calculatedAttributesVertex.dataCopiedMessage"),
  });

  const copyToBuffer = useCallback(() => {
    copyContentToBuffer();
    setCopied(true);
  }, [input]);

  useEffect(() => {
    setCopied(false);
  }, [format]);

  const formatedValue = useMemo(() => {
    switch (format) {
      case "WKT":
        return getWktFormat(value);
      case "KML":
        return getKmlFormat(value);
      default:
        return getGeoJsonFormat(value);
    }
  }, [format, value]);

  return {
    copyToBuffer,
    formatedValue,
    input,
  };
};
